'use client';

import { useState } from 'react';
import { Produto, getFaixaEtariaInfo, getTagNutricionalInfo } from '@/app/lib/papa/definitions';
import { useCart } from '@/app/lib/papa/cardapio/cart-context';
import './size-selection-modal.css';
import { Add, Remove } from '@mui/icons-material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Image from 'next/image';

interface SizeSelectionModalProps {
    produto: Produto;
    onClose: () => void;
}

function formatCurrency(value: number) {
    return 'R$ ' + value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function SizeSelectionModal({ produto, onClose }: SizeSelectionModalProps) {
    const tamanhosVisiveis = (produto.tamanhos || []).filter(t => t.visivel !== false);
    const defaultTamanho = tamanhosVisiveis[0] || produto.tamanhos[0];
    const [selectedTamanho, setSelectedTamanho] = useState(defaultTamanho);
    const [quantity, setQuantity] = useState(1);
    const { addToCart } = useCart();

    const fotos = produto.fotos && produto.fotos.length > 0 ? produto.fotos : (produto.foto_url ? [produto.foto_url] : []);
    const [activeFotoIndex, setActiveFotoIndex] = useState(0);

    const maxEstoque = (selectedTamanho?.gerenciar_estoque !== false && selectedTamanho?.quantidade_atual !== undefined)
        ? selectedTamanho.quantidade_atual
        : 999;

    const handleIncrement = () => {
        setQuantity((prev) => (prev < maxEstoque ? prev + 1 : prev));
    };

    const handleDecrement = () => setQuantity((prev) => (prev > 1 ? prev - 1 : 1));

    const handlePrevPhoto = (e: React.MouseEvent) => {
        e.stopPropagation();
        setActiveFotoIndex((prev) => (prev > 0 ? prev - 1 : fotos.length - 1));
    };

    const handleNextPhoto = (e: React.MouseEvent) => {
        e.stopPropagation();
        setActiveFotoIndex((prev) => (prev < fotos.length - 1 ? prev + 1 : 0));
    };

    const handleAddToCart = () => {
        if (!selectedTamanho || selectedTamanho.visivel === false) {
            return;
        }
        for (let i = 0; i < quantity; i++) {
            addToCart(produto, selectedTamanho);
        }
        onClose();
    };

    const faixaInfo = getFaixaEtariaInfo(produto.faixa_etaria);
    const tagsNutricionais = Array.isArray(produto.tags_nutricionais) ? produto.tags_nutricionais : [];

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                
                {/* Galeria / Carrossel de Fotos do Produto */}
                {fotos.length > 0 && (
                    <div className="mb-4">
                        <div className="group relative w-full h-52 sm:h-60 rounded-2xl overflow-hidden bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm">
                            <Image
                                unoptimized
                                src={fotos[activeFotoIndex]}
                                alt={`${produto.titulo} - Foto ${activeFotoIndex + 1}`}
                                fill
                                className="object-cover transition-all duration-300"
                            />

                            {/* Controles de Navegação (Setas) */}
                            {fotos.length > 1 && (
                                <>
                                    <button
                                        type="button"
                                        onClick={handlePrevPhoto}
                                        className="absolute left-2.5 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-all cursor-pointer shadow-md opacity-90 hover:opacity-100"
                                        title="Foto anterior"
                                    >
                                        <ChevronLeftIcon fontSize="small" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleNextPhoto}
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-all cursor-pointer shadow-md opacity-90 hover:opacity-100"
                                        title="Próxima foto"
                                    >
                                        <ChevronRightIcon fontSize="small" />
                                    </button>

                                    {/* Contador de Fotos */}
                                    <div className="absolute top-2.5 right-2.5 px-2.5 py-1 rounded-full bg-black/65 backdrop-blur-xs text-white font-mono font-bold text-xs shadow-xs">
                                        {activeFotoIndex + 1} / {fotos.length}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Miniaturas de Navegação Rápida */}
                        {fotos.length > 1 && (
                            <div className="flex items-center justify-center gap-2 mt-2.5 overflow-x-auto py-1">
                                {fotos.map((f, idx) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        onClick={() => setActiveFotoIndex(idx)}
                                        className={`w-12 h-12 rounded-xl overflow-hidden relative border-2 transition-all cursor-pointer shrink-0 ${
                                            activeFotoIndex === idx 
                                                ? 'border-[#EF4444] scale-105 shadow-xs ring-2 ring-red-500/20' 
                                                : 'border-slate-200 dark:border-slate-700 opacity-60 hover:opacity-100'
                                        }`}
                                    >
                                        <Image unoptimized src={f} alt={`Thumbnail ${idx + 1}`} fill className="object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Banner de Faixa Etária e Nutrição */}
                {(faixaInfo || tagsNutricionais.length > 0) && (
                    <div className="mb-3 space-y-2">
                        {faixaInfo && (
                            <div className={`p-2.5 rounded-xl border flex items-center gap-2.5 ${faixaInfo.badgeBg} ${faixaInfo.badgeBorder}`}>
                                <span className="text-xl shrink-0">{faixaInfo.emoji}</span>
                                <div>
                                    <h4 className={`text-xs font-bold ${faixaInfo.badgeText}`}>
                                        Recomendado: {faixaInfo.label}
                                    </h4>
                                    <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-tight">
                                        {faixaInfo.sublabel}
                                    </p>
                                </div>
                            </div>
                        )}

                        {tagsNutricionais.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                                {tagsNutricionais.map((tagId) => {
                                    const tagInfo = getTagNutricionalInfo(tagId);
                                    if (!tagInfo) return null;
                                    return (
                                        <span
                                            key={tagId}
                                            className={`px-2.5 py-1 rounded-full text-xs font-bold border flex items-center gap-1 shadow-2xs ${tagInfo.badgeBg} ${tagInfo.badgeBorder} ${tagInfo.badgeText}`}
                                            title={tagInfo.description}
                                        >
                                            <span>{tagInfo.emoji}</span>
                                            <span>{tagInfo.label}</span>
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    {produto.is_kit && <span className="text-lg">🎁</span>}
                    {produto.titulo}
                </h2>
                {produto.descricao && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-3">{produto.descricao}</p>
                )}

                {/* Seção Especial de Itens Inclusos no Kit */}
                {produto.is_kit && Array.isArray(produto.itens) && produto.itens.length > 0 && (
                    <div className="mb-4 p-3 bg-amber-50/70 dark:bg-amber-950/30 rounded-xl border border-amber-200 dark:border-amber-800">
                        <div className="text-xs font-bold text-amber-900 dark:text-amber-300 mb-2 flex items-center justify-between">
                            <span>📦 Itens Inclusos neste Combo:</span>
                            <span className="text-[10px] font-semibold text-amber-700 dark:text-amber-400">
                                {produto.itens.reduce((acc: number, i: any) => acc + Number(i.quantidade || 1), 0)} itens no total
                            </span>
                        </div>
                        <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                            {produto.itens.map((comp: any, idx: number) => (
                                <div
                                    key={idx}
                                    className="flex items-center justify-between gap-2 p-1.5 bg-white dark:bg-slate-900 rounded-lg border border-amber-100 dark:border-amber-900/60 text-xs"
                                >
                                    <div className="flex items-center gap-2 overflow-hidden">
                                        <div className="w-7 h-7 rounded-md bg-slate-100 dark:bg-slate-800 relative overflow-hidden shrink-0 border border-slate-200 dark:border-slate-700">
                                            {comp.foto_url ? (
                                                <Image
                                                    src={comp.foto_url}
                                                    alt={comp.produto_titulo || 'Item'}
                                                    fill
                                                    className="object-cover"
                                                    sizes="28px"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-[10px]">🍲</div>
                                            )}
                                        </div>
                                        <div className="truncate">
                                            <span className="font-bold text-slate-800 dark:text-slate-100">
                                                {comp.quantidade}x
                                            </span>{' '}
                                            <span className="text-slate-700 dark:text-slate-300">
                                                {comp.produto_titulo}
                                            </span>
                                            {comp.tamanho_nome && (
                                                <span className="text-[10px] text-slate-400 ml-1">
                                                    ({comp.tamanho_nome})
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 shrink-0">
                                        Incluso
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="tamanhos-list">
                    {tamanhosVisiveis.map(tamanho => {
                        const poucasUnidades = tamanho.gerenciar_estoque !== false && (tamanho.quantidade_atual ?? 0) <= 5 && (tamanho.quantidade_atual ?? 0) > 0 && tamanho.visivel !== false;

                        return (
                            <div key={tamanho.id} className="tamanho-item">
                                <label>
                                    <input
                                        type="radio"
                                        name="tamanho"
                                        value={tamanho.id}
                                        disabled={tamanho.visivel === false}
                                        checked={selectedTamanho?.id === tamanho.id}
                                        onChange={() => {
                                            setSelectedTamanho(tamanho);
                                            setQuantity(1);
                                        }}
                                    />
                                    <span className="tamanho-nome flex items-center gap-2">
                                        <span>{tamanho.nome}</span>
                                        {poucasUnidades && (
                                            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/60 px-1.5 py-0.5 rounded-md border border-amber-200 dark:border-amber-800 shadow-2xs">
                                                ⚡ Restam {tamanho.quantidade_atual} un
                                            </span>
                                        )}
                                    </span>
                                    {tamanho.visivel === false && <span className="md:ml-2 text-sm font-medium text-red-500">Esgotado</span>}

                                    {tamanho.visivel !== false && (
                                        tamanho.valor_desconto && tamanho.valor_desconto > 0 && tamanho.valor_desconto !== tamanho.valor ? (
                                            <div className="tamanho-preco">
                                                <span className="produto-valor-original whitespace-nowrap sr-only sm:not-sr-only">{formatCurrency(tamanho.valor)}</span>
                                                <span className="produto-valor-desconto whitespace-nowrap">{formatCurrency(tamanho.valor_desconto)}</span>
                                            </div>
                                        ) : (
                                            <span className="tamanho-preco whitespace-nowrap">{formatCurrency(tamanho.valor)}</span>
                                        )
                                    )}
                                </label>
                            </div>
                        );
                    })}
                </div>

                <div className="flex items-center justify-center gap-4 py-4">
                    <button
                        onClick={handleDecrement}
                        className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 transition-colors disabled:opacity-50 cursor-pointer"
                        disabled={quantity <= 1 || selectedTamanho?.visivel === false}
                    >
                        <Remove />
                    </button>
                    <span className="text-xl font-bold min-w-[2rem] text-center">{quantity}</span>
                    <button
                        onClick={handleIncrement}
                        className="w-10 h-10 rounded-full bg-[#EF4444] hover:bg-[#DC2626] flex items-center justify-center text-white shadow-md transition-colors disabled:opacity-50 cursor-pointer"
                        disabled={selectedTamanho?.visivel === false || quantity >= maxEstoque}
                        title={quantity >= maxEstoque ? 'Limite de estoque disponível atingido' : 'Adicionar mais um'}
                    >
                        <Add />
                    </button>
                </div>

                <button
                    onClick={handleAddToCart}
                    disabled={!selectedTamanho || selectedTamanho.visivel === false}
                    className="add-to-cart-button disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                    {!selectedTamanho || selectedTamanho.visivel === false ? 'Opção Esgotada' : 'Adicionar ao carrinho'}
                </button>
                <button onClick={onClose} className="close-button cursor-pointer">
                    Fechar
                </button>
            </div>
        </div>
    );
}
